import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Zap, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null); // { exists, has_password, name }
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Récupérer les données de redirection si présentes
  const returnTo = location.state?.returnTo;
  const savedPromoCode = location.state?.promoCode;
  const savedSelectedPlan = location.state?.selectedPlan;

  // Au chargement, pré-remplir avec les données du diagnostic
  useEffect(() => {
    const diagnosticUser = localStorage.getItem('diagnostic_user');
    if (diagnosticUser) {
      try {
        const { email, name } = JSON.parse(diagnosticUser);
        setFormData(prev => ({
          ...prev,
          email: email || '',
          name: name || ''
        }));
        // Basculer automatiquement en mode inscription si on a des données
        if (email) {
          setIsLogin(false);
          // Vérifier l'état du compte
          checkAccountStatus(email);
        }
      } catch (e) {
        console.error('Error parsing diagnostic user data:', e);
      }
    }
  }, []);

  // Vérifier l'état du compte quand l'email change (en mode inscription)
  const checkAccountStatus = async (email) => {
    if (!email || email.length < 5) {
      setAccountStatus(null);
      return;
    }
    
    setCheckingAccount(true);
    try {
      const response = await axios.post(`${API}/auth/check-account`, { email: email.trim().toLowerCase() });
      setAccountStatus(response.data);
      
      // Si le compte existe avec mot de passe, basculer en mode connexion
      if (response.data.exists && response.data.has_password) {
        setIsLogin(true);
        toast.info('Ce compte existe déjà. Connectez-vous avec votre mot de passe.');
      }
    } catch (error) {
      console.error('Error checking account:', error);
      setAccountStatus(null);
    } finally {
      setCheckingAccount(false);
    }
  };

  // Débouncer la vérification de l'email
  useEffect(() => {
    if (isLogin) return; // Ne pas vérifier en mode connexion
    
    const timer = setTimeout(() => {
      if (formData.email) {
        checkAccountStatus(formData.email);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData.email, isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/signup';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Nettoyer les données du diagnostic
      localStorage.removeItem('diagnostic_user');
      
      const successMessage = isLogin 
        ? 'Connexion réussie !' 
        : (accountStatus?.exists && !accountStatus?.has_password)
          ? 'Mot de passe créé avec succès !'
          : 'Compte créé avec succès !';
      
      toast.success(successMessage);
      
      // Rediriger vers la page demandée ou le dashboard
      if (returnTo) {
        navigate(returnTo, { state: { promoCode: savedPromoCode, selectedPlan: savedSelectedPlan } });
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // Déterminer le texte à afficher
  const isCompletingAccount = !isLogin && accountStatus?.exists && !accountStatus?.has_password;
  const title = isLogin 
    ? 'Connexion' 
    : isCompletingAccount 
      ? 'Finalisez votre inscription'
      : 'Créer un compte';
  
  const submitButtonText = isLogin 
    ? 'Se connecter' 
    : isCompletingAccount 
      ? 'Créer mon mot de passe'
      : 'Créer mon compte';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4" data-testid="logo-link">
            <div className="w-12 h-12 bg-primary rounded-md flex items-center justify-center">
              <Zap className="w-7 h-7 text-black" />
            </div>
            <span className="text-3xl font-heading font-bold">Boost-StrategIA</span>
          </Link>
          <h1 className="text-2xl font-heading font-semibold mt-6" data-testid="auth-title">
            {title}
          </h1>
          
          {/* Message pour compte à compléter */}
          {isCompletingAccount && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 bg-primary/10 border border-primary/30 rounded-lg"
            >
              <div className="flex items-center justify-center gap-2 text-primary">
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-medium">
                  Bienvenue {accountStatus.name || ''} ! Plus qu'une étape.
                </span>
              </div>
              <p className="text-xs text-muted mt-1">
                Créez votre mot de passe pour accéder à votre espace.
              </p>
            </motion.div>
          )}
        </div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-card border border-border rounded-lg p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            {!isLogin && (
              <div>
                <Label htmlFor="name" data-testid="name-label">Nom complet</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isCompletingAccount}
                  disabled={isCompletingAccount && accountStatus?.name}
                  className="mt-2"
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" data-testid="email-label">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={isCompletingAccount}
                className="mt-2"
                data-testid="email-input"
              />
              {checkingAccount && (
                <p className="text-xs text-muted mt-1">Vérification...</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" data-testid="password-label">
                {isCompletingAccount ? 'Choisissez votre mot de passe' : 'Mot de passe'}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-2"
                placeholder={isCompletingAccount ? 'Minimum 6 caractères' : ''}
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full shadow-glow-md"
              disabled={loading || checkingAccount}
              data-testid="auth-submit-button"
            >
              {loading ? 'Chargement...' : submitButtonText}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setAccountStatus(null);
              }}
              className="text-sm text-muted hover:text-primary transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Auth;
